import type { ISectionIASMetrics } from '@services/types';
import { ClockCircleOutlined, SafetyOutlined } from 'yoc-ui-library/icons';

import { classesBuilder, formatNumber } from '@common/helpers';
import Card from '@components/Card';
import Icon from '@components/Icon';
import type { ISectionComponentProps } from '@components/ReportContent/types';

import '../section.scss';
import style from './styles.module.scss';

const IASMetricsSection = ({ sectionData }: ISectionComponentProps) => {
  if (!('data' in sectionData) || !sectionData.data) {
    return <div>Error: Invalid data for IAS Metrics section.</div>;
  }

  const { data } = sectionData as ISectionIASMetrics;

  return (
    <div className={classesBuilder('section-wrapper', style['ias-wrapper'])}>
      <div className="section-header">
        <h3 className="section-subtitle">Campaign Review</h3>
        <h1 className="section-title">IAS METRIC</h1>
      </div>

      <div className={style['ias-grid']}>
        <div className={style.viewability}>
          <Card>
            <div className={style['metric-card']}>
              <h2 className={style['metric-header']}>Viewability</h2>
              <div className={style['metrics-container']}>
                <div className={style['metric-item']}>
                  <div className={style['metric-headline']}>
                    <SafetyOutlined />
                    <span className={style['metric-label']}>Measured Ads</span>
                  </div>
                  <p className={style['metric-value']}>{formatNumber(data.measured_ads)}</p>
                  <p className={style['metric-description']}>
                    The total number of unblocked ads in the campaign with all detected invalid traffic removed, where
                    IAS was able to collect viewability data.
                  </p>
                </div>
                <div className={style.divider} />
                <div className={style['metric-item']}>
                  <div className={style['metric-headline']}>
                    <Icon name="statistics" />
                    <span className={style['metric-label']}>Viewability Rate</span>
                  </div>
                  <p className={style['metric-value']}>
                    {formatNumber(data.viewability_rate || '0.894', { style: 'percent', fractionDigits: 1 })}
                  </p>
                  <p className={style['metric-description']}>
                    The rate of unblocked ads in the campaign with all detected invalid traffic removed, where IAS was
                    able to collect viewability data.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className={style['quality-impressions']}>
          <Card>
            <div className={style['metric-card']}>
              <h2 className={style['metric-header']}>Quality Impressions</h2>
              <div className={style['metric-item']}>
                <div className={style['metric-headline']}>
                  <Icon name="eye" />
                  <span className={style['metric-label']}>Quality Impressions</span>
                </div>
                <p className={style['metric-value']}>{formatNumber(data.quality_impressions)}</p>
                <p className={style['metric-description']}>
                  The number of ads that passed all selected Quality criteria
                </p>
              </div>
            </div>
          </Card>
        </div>
        <div className={style['time-in-view']}>
          <Card>
            <div className={style['metric-card']}>
              <h2 className={style['metric-header']}>Time-in-view</h2>
              <div className={style['metric-item']}>
                <div className={style['metric-headline']}>
                  <ClockCircleOutlined />
                  <span className={style['metric-label']}>Average Time In View</span>
                </div>
                <p className={style['metric-value']}>{data.average_time_in_view || '19.1s'}</p>
                <p className={style['metric-description']}>
                  The average duration, based on the arithmetic, means that a viewable impression remained in-view
                  within your campaign.
                </p>
              </div>
            </div>
          </Card>
        </div>
        <div className={style['brand-safety']}>
          <Card>
            <div className={style['metric-card']}>
              <h2 className={style['metric-header']}>Brand Safety</h2>
              <div className={style['metric-item']}>
                <div className={style['metric-headline']}>
                  <Icon name="statistics" />
                  <span className={style['metric-label']}>Pass Rate</span>
                </div>
                <p className={style['metric-value']}>
                  {formatNumber(data.pass_rate || '0.998', { style: 'percent', fractionDigits: 1 })}
                </p>
                <p className={style['metric-description']}>
                  The percentage of ads that IAS passed all valid traffic, and safety, from keywords, domain URL, geo,
                  mobile URL or content language, of all ads IAS processed.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default IASMetricsSection;
